/**
 * Grafana Faro — frontend error logs into our self-hosted Loki (viewable in
 * Grafana). Runs alongside Sentry while we confirm logs land in Grafana.
 *
 * Errors only: only `ErrorsInstrumentation` (uncaught errors + unhandled
 * rejections) is registered — no web-vitals / session / view / console
 * instrumentation. The SDK POSTs directly to the receiver `url`, sending the
 * API key as the `x-api-key` header (the receiver requires it).
 *
 * `preserveOriginalError` exposes the original Error in `beforeSend`, so we
 * apply the SAME policy as Sentry: drop expected mock-API failures (see
 * `api/expectedError`) and scrub JWT / email / phone (security.md §7 / §13).
 */

import { ErrorsInstrumentation, type TransportItem, initializeFaro } from '@grafana/faro-web-sdk';
import { isExpectedApiError } from './api/expectedError';
import { config } from './config';
import { registerErrorSink, scrub } from './log';

type ExceptionPayload = {
  originalError?: unknown;
  value?: string;
};

let initialised = false;

export function initFaro(): void {
  const { url, apiKey } = config.telemetry.faro;
  if (!url || initialised) return;
  initialised = true;

  const faro = initializeFaro({
    url,
    apiKey: apiKey || undefined,
    app: {
      name: 'flysafe-v2',
      version: config.appVersion,
      // Filterable in Grafana: `airmarket`, `sait-dev`, etc.
      environment: config.isDev ? `${config.tenant.id}-dev` : config.tenant.id,
    },
    instrumentations: [new ErrorsInstrumentation()],
    preserveOriginalError: true,
    beforeSend: (item: TransportItem) => {
      const payload = item.payload as ExceptionPayload;
      // Drop expected failures from the not-yet-real mock endpoints.
      if (isExpectedApiError(payload.originalError)) return null;
      if (typeof payload.value === 'string') payload.value = scrub(payload.value);
      return item;
    },
  });

  // Route explicit log.error(...) calls into Faro (the indirection keeps
  // @grafana/faro-web-sdk out of log.ts / the MQTT worker bundle).
  registerErrorSink((msg, ctx) => {
    faro.api.pushError(new Error(msg), {
      context: ctx
        ? Object.fromEntries(Object.entries(ctx).map(([k, v]) => [k, String(v)]))
        : undefined,
    });
  });
}
